import React, { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/images/inmate-white-logo.png';
import { KioskNoticeActions } from '../../_store/KioskNotice.slice';
import { useDispatch, useSelector } from 'react-redux';
import useKioskRestrictIntervalValidation from '../../hooks/useKioskRestrictIntervalValidation';
import Loader from '../../components/common/Loader';
import { authActions } from '../../_store';
import { getStoredValue, removeStoredValue } from "../../_helpers/storageHelper";

const KioskNotice = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { noticeDetails: notices } = useSelector(state => state.kioskNotice);
    const { loginDetails } = useSelector(state => state.kioskLogin);
    const storedLoginDetails = loginDetails?.FacilityID ? loginDetails : getStoredValue("loginDetails");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [sortedNotices, setSortedNotices] = useState([]);
    const validateKioskRestrictInterval = useKioskRestrictIntervalValidation();
    const [isLoading, setisLoading] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [timeLeft, setTimeLeft] = useState(900); // Set to 10 seconds

    useEffect(() => {
        if (notices?.length > 0) {
            // Sort by StartDate in ascending order
            const sortedArray = [...notices].sort((a) => a.NoticeID);
            setSortedNotices(sortedArray);
        }
    }, [notices]);

    const handleNext = () => {
        if (currentIndex < sortedNotices?.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    useEffect(() => {
        const loadNotices = async () => {
            setisLoading(true)
            const kioskCode = storedLoginDetails?.KiosksCode;
            const facilityId = storedLoginDetails?.FacilityID;
            dispatch(KioskNoticeActions.getLoadKiosksNoticeDisplayResult({ kioskCode, facilityId })).then(x => {
                if (x?.payload?.length == 0) {
                    navigate('/login')
                }
                setisLoading(false);
            });
        };
        loadNotices();
    }, [loginDetails]);

    useEffect(() => {
        let inactivityTimer;
        const resetTimer = () => {
            setIsActive(true);
            setTimeLeft(900); // Reset timer to 10 seconds
            clearTimeout(inactivityTimer);
            // inactivityTimer = setTimeout(handleLogoutWarning, 5 * 1000); // Show warning after 5 seconds
        };

        window.addEventListener('mousemove', resetTimer);
        window.addEventListener('keydown', resetTimer);
        window.addEventListener('click', resetTimer);

        // inactivityTimer = setTimeout(handleLogoutWarning, 5 * 1000); // Show warning after 5 seconds

        return () => {
            clearTimeout(inactivityTimer);
            window.removeEventListener('mousemove', resetTimer);
            window.removeEventListener('keydown', resetTimer);
            window.removeEventListener('click', resetTimer);
        };

    }, []);

    useEffect(() => {
        if (!isActive || timeLeft <= 0) return;

        const countdownInterval = setInterval(() => {
            setTimeLeft(prevTime => {
                if (prevTime <= 1) {
                    clearInterval(countdownInterval);
                    setIsActive(false);
                    dispatch(authActions.logout()); // Log out when time reaches zero
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);

        return () => clearInterval(countdownInterval);
    }, [isActive, timeLeft]);

    useEffect(() => {
        // Function to execute on page refresh
        const handlePageRefresh = () => {
            removeStoredValue("inmateMenuList"); removeStoredValue("loginResponse"); removeStoredValue("KioskRestrictIntervalValidate");
            navigate('/');
        };

        // Check if the page has been refreshed
        if (sessionStorage.getItem('isRefreshed')) {
            handlePageRefresh();
        } else {
            sessionStorage.setItem('isRefreshed', 'true');
        }

        return () => {
            sessionStorage.removeItem('isRefreshed');
        };
    }, []);

    // const handleNotice = (editedValue) => {
    //     if (typeof editedValue === "string") {
    //         try {
    //             // Try parsing as JSON
    //             const parsedNotice = JSON.parse(editedValue);

    //             // Check if it has a valid Draft.js format
    //             if (parsedNotice.blocks && Array.isArray(parsedNotice.blocks)) {
    //                 return parsedNotice.blocks.map(block => block.text).join("<br>");
    //             }
    //         } catch (error) {
    //             // If JSON parsing fails, return the string as is
    //             return editedValue;
    //         }
    //     }
    //     return "";
    // };

    const handleNotice = (editedValue) => {
        if (typeof editedValue === "string") {
            try {
                // Try parsing as JSON
                const parsedNotice = JSON.parse(editedValue);

                // Check if it has a valid Draft.js format
                if (parsedNotice.blocks && Array.isArray(parsedNotice.blocks)) {
                    return parsedNotice.blocks.map(block => block.text).join("<br>");
                }
            } catch (error) {

                if (editedValue.includes('ql-font-')) {
                    return convertQuillToHtml(editedValue);
                }
                // If JSON parsing fails, return the string as is
                return editedValue;
            }
        }
        return "";
    };

    function convertQuillToHtml(quillHtml) {
        const fontMapping = {
            "ql-font-wingdings": "Wingdings, cursive",
            "ql-font-courier-new": "'Courier New', monospace",
            "ql-font-georgia": "Georgia, serif",
            "ql-font-tahoma": "Tahoma, sans-serif",
            "ql-font-times-new-roman": "'Times New Roman', serif",
            "ql-font-verdana": "Verdana, sans-serif",
            "ql-font-impact": "Impact, sans-serif"
        };

        const alignmentMapping = {
            "ql-align-center": "text-align: center;",
            "ql-align-right": "text-align: right;",
            "ql-align-justify": "text-align: justify;"
        };

        const indentRegex = /ql-indent-(\d+)/;

        for (const [qlClass, fontStyle] of Object.entries(fontMapping)) {
            const regex = new RegExp(`<([^>]+)class=["']?([^"'>]*?)\\b${qlClass}\\b([^"'>]*)["']?([^>]*)>`, "g");
            quillHtml = quillHtml.replace(regex, (match, beforeTag, beforeClass, afterClass, afterTag) => {
                let styleMatch = match.match(/style=["']([^"']*)["']/);
                let newStyle = styleMatch ? `${styleMatch[1]}; font-family: ${fontStyle};` : `font-family: ${fontStyle};`;
                let result = match.replace(/class=["'][^"']*["']/, '').replace(/style=["'][^"']*["']/, '');
                return result.replace(`<${beforeTag}`, `<${beforeTag} style="${newStyle}"`);
            });
        }

        // Handle alignments
        for (const [qlClass, alignStyle] of Object.entries(alignmentMapping)) {
            const regex = new RegExp(`<([^>]+)class=["']?([^"'>]*?)\\b${qlClass}\\b([^"'>]*)["']?([^>]*)>`, "g");
            quillHtml = quillHtml.replace(regex, (match, beforeTag, beforeClass, afterClass, afterTag) => {
                let styleMatch = match.match(/style=["']([^"']*)["']/);
                let newStyle = styleMatch ? `${styleMatch[1]}; ${alignStyle}` : alignStyle;
                let result = match.replace(/class=["'][^"']*["']/, '').replace(/style=["'][^"']*["']/, '');
                return result.replace(`<${beforeTag}`, `<${beforeTag} style="${newStyle}"`);
            });
        }

        // Handle indents
        quillHtml = quillHtml.replace(/class=["']?([^"'>]*?(ql-indent-\d+)[^"'>]*)["']?/g, (match, classStr, indentClass) => {
            const indentLevel = parseInt(indentClass.split('-')[2]);
            const margin = `${indentLevel * 3}em`;
            if (match.includes("style=")) {
                return match.replace(/style=["']([^"']*)["']/, (s, style) => `style="${style}; margin-left: ${margin};"`);
            } else {
                return `style="margin-left: ${margin};"`;
            }
        });

        return quillHtml;
    }

    return (
        <div className="parent-container">
            <div className="form-column">
                <img className='image' onClick={() => { navigate('/') }} src={logo} alt="Logo" />
                {sortedNotices?.length > 0 &&
                    <div className="notice-card">
                        {/* Title */}
                        <h2 className="find-inmate-head mb-3">
                            {sortedNotices[currentIndex]?.NoticeTitle}
                        </h2>

                        {/* Scrollable content area */}
                        <div className="notice-content">
                            <div dangerouslySetInnerHTML={{ __html: handleNotice(sortedNotices[currentIndex]?.NoticeDispaly) }} /></div>

                        {/* Footer section positioned at the bottom of the card */}
                        <div id="footer" className="notice-footer">
                            <div className="align-self-start">
                                {currentIndex > 0 && (
                                    <button title='Previous' onClick={handlePrevious} className="btn btn-danger submit-btn" >
                                        <i className="fa fa-backward"></i> Previous
                                    </button>
                                )}
                            </div>
                            <div className="align-self-end">
                                {currentIndex < sortedNotices?.length - 1 ? (
                                    <button title='Next' onClick={handleNext} className="btn btn-danger submit-btn">
                                        Next <i className="fa fa-forward"></i>
                                    </button>
                                ) : (
                                    <button title='OK' className="btn btn-success submit-btn" onClick={() => (navigate('/login'))}>
                                        OK
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                }
                {isLoading && <Loader />}
            </div>

        </div>

    );
};

export default memo(KioskNotice);
